import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { VideoPlayer } from "@/components/common/video-player";
import { DeleteConfirmModal, Spinner } from "@/components/ui/modal";
import { RefreshIcon, TrashIcon, WarningRedIcon } from "@/components/ui/icon";
import { TruncatedText } from "@/components/common/truncated-text";
import { Image } from "@/components/common/image";
import { IconButton } from "@/components/common/icon-button";
import unusual_cover from '@/assets/actor/unusual_cover.webp';
import { TextEditable } from "@/components/common/text-editable";
import { EditIcon } from "../agent-dashboard/assets";
import { Check } from "lucide-react";

interface ActorVideoCardProps {
  className?: string;
  actor: Actor;
  name: string;
  url: string;
  poster: string;
  status: number;
  createdAt: string;
  isSelected?: boolean;
  onClick?: () => void;
  onRename?: (name: string) => void;
  onRegenerate?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
}

export const ActorCard = ({
  className = "",
  actor,
  url,
  poster,
  status,
  createdAt,
  isSelected,
  name,
  onClick,
  onRename,
  onRegenerate,
  onDelete,
  onEdit,
}: ActorVideoCardProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const isSuccess = status === 2;
  const isProcessing = status < 2;
  const isFailed = status > 2;
  const isUnusual = actor.isUnusual;

  const handleRename = (name: string) => {
    setIsEditing(false);
    onRename?.(name);
  };

  const handleRegenerateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRegenerate) {
      onRegenerate();
    }
  };

  if (isUnusual) {
    return (
      <a href="mailto:support@bizdesk.ai" target="_blank">
        <div className="w-full h-full relative">
          <div
            className={cn(
              "group relative bg-card border border-border rounded-md w-full h-full aspect-[9/16] overflow-hidden",
              "flex flex-col items-center text-center cursor-pointer",
              className
            )}
          >
            <div className="relative w-full h-full">
              {<Image src={poster || unusual_cover} className="w-full h-full rounded-sm object-cover" />}
            </div>
            <div className={cn(
              "absolute -inset-0.5 rounded-lg flex flex-col items-center justify-center px-1 space-y-2",
              poster && "bg-black/30 backdrop-blur-sm"
            )}>
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/30 backdrop-filter backdrop-blur-lg">
                <WarningRedIcon className="w-4 h-4 text-white" />
              </div>
              <span className="text-white text-xs font-medium underline">
                {t("assets.video_card.unusual_message")}
              </span>
            </div>
            <div
              className="opacity-0 group-hover:opacity-100 absolute top-1 right-1 transition-all"
              onClick={(e) => e.stopPropagation()}
            >
              <DeleteConfirmModal
                trigger={
                  <IconButton
                    className="hover:bg-white/20"
                    icon={<TrashIcon className="w-4 h-4 text-white" />}
                  />
                }
                onConfirm={() => onDelete?.()}
              />
            </div>
          </div>
          <div className="absolute -bottom-2 w-full text-start translate-y-full">
            <TruncatedText
              text={name}
              className="text-sm font-medium mb-1 max-w-[90%] truncate !p-0 overflow-hidden rounded-none"
            />
          </div>
        </div>
      </a>
    );
  }

  return (
    <div className="w-full h-full relative">
      <div
        className={cn(
          "group relative bg-card rounded-md w-full h-full aspect-[9/16] overflow-hidden",
          "transition-all duration-200",
          "flex flex-col items-center text-center",
          onClick && "cursor-pointer",
          className,
          "p-0.5 rounded-sm transition-all duration-150",
          isSelected
            ? "outline outline-2 outline-i-normal"
            : "outline outline-transparent"
        )}
        onClick={onClick}
      >
        {/* 视频容器 */}
        <div className="relative w-full h-full">
          <VideoPlayer
            src={url}
            className="w-full h-full rounded-sm"
            poster={poster}
            showControlsBar={false}
          />

          {isSuccess && (
            <>
              <div
                className={cn(
                  "size-11 bg-black  absolute -bottom-6 -right-[22px] opacity-0 transition-all duration-150 rounded-tl-[18.4px] rounded-br-[10px] rounded-tr-none rounded-bl-none",
                  isSelected && "opacity-100"
                )}
              >
              </div>
              <Check className={cn(
                "size-4 text-white absolute bottom-[0.5px] right-[0.5px] font-bold opacity-0",
                isSelected && "opacity-100"
              )} />
            </>
          )}

          {/* 生成中状态覆盖层 */}
          {isProcessing && (
            <div className="absolute rounded-sm inset-0 bg-black/40">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <Spinner className="w-6 h-6 text-white" />
                <p className="px-1 text-white font-medium text-xs">
                  {actor.generateType === 2
                    ? t("assets.video_card.processing_message_image")
                    : t("assets.video_card.processing_message_video")}
                </p>
              </div>
            </div>
          )}

          {/* 失败状态覆盖层 */}
          {isFailed && (
            <div
              className="absolute inset-0 bg-black/60 rounded-sm flex flex-col items-center justify-center"
              onClick={handleRegenerateClick}
            >
              {/* 重新生成按钮 */}
              <button className="flex flex-col items-center justify-center px-1 gap-2">
                <RefreshIcon className="w-8 h-8" />
                <span className="text-i-white text-xs font-medium">
                  {t("assets.video_card.failed_message")}
                </span>
              </button>
            </div>
          )}
        </div>
        {!isProcessing && <div
          className="opacity-0 group-hover:opacity-100 absolute top-1 right-1 transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            className="hover:bg-white/20 bg-[rgba(0,0,0,0.2)]"
            icon={<EditIcon className="w-4 h-4 text-white" />}
            onClick={() => onEdit?.()}
          />
        </div>}
      </div>
      <div className="absolute -bottom-1 w-full translate-y-full text-center">
        {/* <TextEditable
          className="text-sm font-medium mb-1 max-w-[90%] truncate !p-0 overflow-hidden rounded-none"
          value={name}
          onChange={(name) => handleRename(name)}
          isEditing={isEditing}
          onStartEditing={() => setIsEditing(true)}
          onEndEditing={() => setIsEditing(false)}
          mode="doubleClick"
        /> */}
        <TruncatedText text={actor.name} className="text-sm text-[#333333]" />
      </div>
    </div>
  );
};
